import {
  CompanyInfo,
  CONDITION_LABEL,
  formatVnd,
  lineAmounts,
  PersonInfo,
  QuoteData,
  compensationValue,
  summarize,
  vndInWords,
} from './calc';

/**
 * Mẫu "Hợp đồng hỗ trợ thiết bị livestream" sinh từ báo giá — HTML dùng cho xem trước, In / Lưu PDF và tải
 * bản Word (.doc). Cơ sở pháp lý + lý do từng điều: docs/bao-gia-thiet-bi/02-nghien-cuu-phap-ly-2026-10-27.md.
 *
 * Mô hình: phần Công ty hỗ trợ = CHO MƯỢN (BLDS Đ.494–499), hết cam kết mà không vi phạm thì TẶNG CHO CÓ ĐIỀU
 * KIỆN (Đ.462); phần Creator tự trả = MUA TRẢ DẦN (Đ.453) có BẢO LƯU QUYỀN SỞ HỮU (Đ.331). Cố ý KHÔNG đưa các
 * điều khoản vô hiệu / trái luật: giữ CCCD gốc, tự vào nhà lấy đồ, bêu tên trên mạng, phạt không giới hạn,
 * lãi > 20%/năm, chiếm tài khoản TikTok.
 *
 * ĐÂY LÀ MẪU THAM KHẢO — luật sư phải rà trước khi dùng chính thức.
 */

const esc = (v: unknown) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const blank = (v?: string, width = 180) =>
  v ? `<b>${esc(v)}</b>` : `<span class="blank" style="min-width:${width}px">&nbsp;</span>`;

function vnDate(iso?: string) {
  if (!iso) return { d: '....', m: '....', y: '........' };
  const [y, m, d] = iso.split('-');
  return { d: d || '....', m: m || '....', y: y || '........' };
}

const showDate = (iso?: string) =>
  iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : iso || '';

function personBlock(p: PersonInfo) {
  return `
  <table class="party">
    <tr><td>Họ và tên:</td><td>${blank(p.fullName, 260)}</td><td>Ngày sinh:</td><td>${blank(
    showDate(p.dob),
    120
  )}</td></tr>
    <tr><td>Số CCCD:</td><td>${blank(p.idNumber, 200)}</td><td>Ngày cấp:</td><td>${blank(
    showDate(p.idIssuedDate),
    120
  )}</td></tr>
    <tr><td>Nơi cấp:</td><td colspan="3">${blank(p.idIssuedPlace, 300)}</td></tr>
    <tr><td>Địa chỉ thường trú:</td><td colspan="3">${blank(p.address, 420)}</td></tr>
    <tr><td>Điện thoại:</td><td>${blank(p.phone, 160)}</td><td>Email:</td><td>${blank(
    p.email,
    160
  )}</td></tr>
  </table>`;
}

const CONTRACT_CSS = `
  @page { size: A4; margin: 18mm 16mm 18mm 20mm; }
  body { font-family: "Times New Roman", Times, serif; font-size: 13.5pt; line-height: 1.45; color: #000; margin: 0; padding: 0 4px; }
  .center { text-align: center; } .r { text-align: right; } .c { text-align: center; }
  h1 { font-size: 16pt; text-align: center; margin: 14px 0 2px; }
  h2 { font-size: 14pt; text-align: center; margin: 18px 0 6px; }
  h3 { font-size: 13.5pt; margin: 14px 0 4px; }
  p { margin: 4px 0; text-align: justify; }
  .motto { font-weight: bold; } .motto2 { font-weight: bold; text-decoration: underline; }
  .blank { display: inline-block; border-bottom: 1px dotted #000; }
  table.grid { border-collapse: collapse; width: 100%; font-size: 12pt; margin: 6px 0; }
  table.grid th, table.grid td { border: 1px solid #000; padding: 3px 5px; vertical-align: top; }
  table.grid th { background: #eee; }
  table.party td { padding: 1px 6px 1px 0; vertical-align: top; }
  .sign { width: 100%; margin-top: 24px; } .sign td { width: 50%; text-align: center; vertical-align: top; height: 130px; }
  .note { font-size: 11pt; font-style: italic; }
  .pb { page-break-before: always; }

  table.handover { table-layout: fixed; font-size: 11.5pt; }
  table.handover td { word-wrap: break-word; }
  .chk { font-family: "Segoe UI Symbol", "DejaVu Sans", sans-serif; }
`;

/** Ô tích in trên giấy: đã tích ☑ / chưa ☐ (KTV vẫn có thể tích tay trên bản in). */
const box = (on?: boolean) => `<span class="chk">${on ? '☑' : '☐'}</span>`;

/**
 * Biên bản bàn giao & nghiệm thu: KTV và Creator cùng kiểm từng thiết bị ngày setup, ký, rồi KTV mang bản giấy
 * về Công ty kèm hợp đồng. Dùng làm Phụ lục 02 của hợp đồng và in riêng (nút "In biên bản bàn giao").
 */
function handoverBody(opts: {
  code: string;
  data: QuoteData;
  company: CompanyInfo;
  title: string;
}): string {
  const { data, company, code, title } = opts;
  const h = data.handover;
  const cr = data.creator;
  const d = vnDate(h?.date);
  const lines = data.lines.filter(
    (l) => !['tháng', 'năm'].includes(l.unit || '') // gói game theo kỳ: không có vật để bàn giao
  );
  return `<h2>${title}</h2>
<div class="center">(Kèm hợp đồng số ${esc(data.contractNo || '..........')} — báo giá ${esc(
    code
  )})</div>
<p>Hôm nay, ngày ${d.d} tháng ${d.m} năm ${d.y}, tại ${blank(data.deliveryAddress, 360)}:</p>
<p><b>Bên giao — Bên A</b> (${esc(company.shortName || company.name)}), kỹ thuật viên: ${blank(
    h?.technicianName,
    200
  )} &nbsp; Điện thoại: ${blank(h?.technicianPhone, 130)}</p>
<p><b>Bên nhận — Bên B</b> (Creator): ${blank(cr.fullName, 220)} &nbsp; TikTok: ${blank(
    cr.tiktokId ? `@${cr.tiktokId.replace(/^@/, '')}` : '',
    150
  )} &nbsp; Điện thoại: ${blank(cr.phone, 130)}</p>
<p>Hai bên cùng kiểm tra và bàn giao các thiết bị sau (cột <b>Tốt</b> = đã kiểm tra, hoạt động bình thường):</p>
<table class="grid handover">
  <colgroup><col style="width:5%"><col style="width:30%"><col style="width:9%"><col style="width:13%"><col style="width:5%"><col style="width:9%"><col style="width:7%"><col style="width:22%"></colgroup>
  <tr><th>STT</th><th>Thiết bị</th><th>Hãng</th><th>Serial</th><th>SL</th><th>Tình trạng</th><th>Tốt</th><th>Ghi chú</th></tr>
  ${lines
    .map((l, i) => {
      const c = h?.checks?.[l.key];
      return `<tr><td class="c">${i + 1}</td><td>${esc(l.name)}</td><td>${
        esc(l.brand) || '&nbsp;'
      }</td><td>${esc(c?.serial || l.serial) || '&nbsp;'}</td><td class="c">${l.qty}</td><td>${
        CONDITION_LABEL[l.condition ?? 'NEW']
      }</td><td class="c">${box(c?.ok)}</td><td>${esc(c?.note) || '&nbsp;'}</td></tr>`;
    })
    .join('')}
</table>
<p>${box(
    h?.trained
  )} Kỹ thuật viên đã lắp đặt, cài đặt phần mềm và <b>hướng dẫn Bên B sử dụng</b> thiết bị.</p>
<p>${box(h?.liveTested)} Đã <b>LIVE thử thành công</b>: hình ảnh, âm thanh, kết nối ổn định.</p>
<p>${box(
    h?.technicianConfirmed
  )} <b>Kỹ thuật viên xác nhận</b> đã kiểm tra đầy đủ từng thiết bị trong danh sách trên.</p>
<p>${box(
    h?.creatorConfirmed
  )} <b>Bên B xác nhận</b> đã nhận đủ thiết bị, đúng tình trạng nêu trên, <b>tất cả hoạt động bình thường</b>. Ảnh / video bàn giao được hai bên lưu giữ.</p>
${h?.note ? `<p>Ghi chú: ${esc(h.note)}</p>` : ''}
<table class="sign"><tr>
  <td><b>KỸ THUẬT VIÊN (ĐẠI DIỆN BÊN A)</b><br><i>(Ký, ghi rõ họ tên)</i></td>
  <td><b>BÊN B (CREATOR)</b><br><i>(Ký, ghi rõ họ tên)</i><br><br><br><br><br>${esc(
    cr.fullName
  )}</td>
</tr></table>
<p class="note">Kỹ thuật viên nộp bản biên bản này (có chữ ký hai bên) về Công ty cùng hợp đồng. Thiết bị đã bàn giao: Bên A giữ quyền sở hữu theo Điều 2 hợp đồng.</p>`;
}

/** Bản in riêng biên bản bàn giao & nghiệm thu. */
export function buildHandoverHtml(opts: {
  code: string;
  data: QuoteData;
  company: CompanyInfo;
}): string {
  return `<!doctype html><html lang="vi"><head><meta charset="utf-8"><title>Biên bản bàn giao ${esc(
    opts.code
  )}</title><style>${CONTRACT_CSS}</style></head><body>
<div class="center motto">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</div>
<div class="center motto2">Độc lập - Tự do - Hạnh phúc</div>
${handoverBody({ ...opts, title: 'BIÊN BẢN BÀN GIAO VÀ NGHIỆM THU THIẾT BỊ' })}
</body></html>`;
}

export function buildContractHtml(opts: {
  code: string;
  data: QuoteData;
  company: CompanyInfo;
}): string {
  const { data, company, code } = opts;
  const s = summarize(data);
  const c = data.contract;
  const p = data.payment;
  const cr = data.creator;
  const date = vnDate(data.signDate);
  const supportedLines = data.lines.filter((l) => l.supportPercent > 0);
  const includedLines = data.hpCreator ? data.lines.filter((l) => l.included) : [];
  const advanceLines = data.lines.filter((l) => l.advance);
  const extraArt1 = includedLines.length || advanceLines.length ? 1 : 0;
  const creatorLines = data.lines.filter((l) => l.supportPercent < 100);
  const methodText =
    p.paymentMethod === 'CASH'
      ? 'Bên B chuyển khoản vào tài khoản của Bên A'
      : p.paymentMethod === 'DEDUCT_INCOME'
      ? 'Bên A trừ dần vào thưởng / thu nhập mà Bên A chi trả cho Bên B'
      : 'Bên B chuyển khoản, phần chưa trả đúng hạn Bên A được trừ vào thưởng / thu nhập của Bên B';
  // Chỉ thiết bị cầm nắm được mới có thể mất / hỏng; dịch vụ, phần mềm, gói tháng / năm không tính đền.
  const physicalLines = data.lines.filter(
    (l) => !['gói', 'tháng', 'năm'].includes(l.unit || '') && !/dịch vụ/i.test(l.category)
  );
  const compensationTotal = physicalLines.reduce((a, l) => a + compensationValue(l, c), 0);
  const extra = (data.extraClauses || '')
    .split('\n')
    .map((t) => t.trim())
    .filter(Boolean);
  let art = 0;
  // Bộ đếm khoản trong Điều 3 (các khoản bật / tắt theo điều khoản).
  let k = 0;
  const h = (title: string) => `<h3>Điều ${++art}. ${title}</h3>`;

  const lineRows = data.lines
    .map((l, i) => {
      const a = lineAmounts(l);
      return `<tr><td class="c">${i + 1}</td><td>${esc(l.name)}</td><td class="c">${
        CONDITION_LABEL[l.condition ?? 'NEW']
      }</td><td class="c">${l.qty}</td><td class="r">${formatVnd(a.amount)}</td><td class="c">${
        l.advance ? 'Ứng trước' : l.included && data.hpCreator ? 'Tặng kèm' : `${l.supportPercent}%`
      }</td><td class="r">${formatVnd(a.company)}</td><td class="r">${formatVnd(
        a.creator
      )}</td></tr>`;
    })
    .join('');

  return `<!doctype html><html lang="vi"><head><meta charset="utf-8"><title>Hợp đồng ${esc(
    data.contractNo || code
  )}</title>
<style>${CONTRACT_CSS}</style></head><body>
<div class="center motto">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</div>
<div class="center motto2">Độc lập - Tự do - Hạnh phúc</div>
<h1>HỢP ĐỒNG HỖ TRỢ THIẾT BỊ LIVESTREAM</h1>
<div class="center">Số: ${blank(data.contractNo, 160)} &nbsp; (theo báo giá ${esc(code)})</div>

<p><i>- Căn cứ Bộ luật Dân sự số 91/2015/QH13;</i></p>
<p><i>- Căn cứ Luật Giao dịch điện tử số 20/2023/QH15;</i></p>
<p><i>- Căn cứ nhu cầu và khả năng của hai bên.</i></p>
<p>Hôm nay, ngày ${date.d} tháng ${date.m} năm ${date.y}, tại ${blank(
    data.signPlace,
    240
  )}, chúng tôi gồm:</p>

<h3>BÊN A (BÊN HỖ TRỢ): ${company.name ? esc(company.name).toUpperCase() : blank('', 320)}</h3>
<table class="party">
  <tr><td>Mã số thuế:</td><td>${blank(company.taxCode, 160)}</td></tr>
  <tr><td>Địa chỉ trụ sở:</td><td>${blank(company.address, 420)}</td></tr>
  <tr><td>Người đại diện:</td><td>${blank(company.representative, 220)} &nbsp; Chức vụ: ${blank(
    company.position,
    120
  )}</td></tr>
  <tr><td>Điện thoại:</td><td>${blank(company.phone, 140)} &nbsp; Email: ${blank(
    company.email,
    180
  )}</td></tr>
  <tr><td>Tài khoản:</td><td>${blank(company.bankAccount, 160)} tại ${blank(
    company.bankName,
    200
  )}</td></tr>${
    company.officeAddress
      ? `
  <tr><td>Văn phòng làm việc:</td><td>${esc(company.officeAddress)}</td></tr>`
      : ''
  }${
    company.shortName
      ? `
  <tr><td>Thương hiệu:</td><td>Hoạt động trên TikTok LIVE với tên <b>${esc(company.shortName)}</b>${
          company.tiktok ? ` (${esc(company.tiktok)})` : ''
        }${company.fanpage ? `; Fanpage: ${esc(company.fanpage)}` : ''}</td></tr>`
      : ''
  }
</table>

<h3>BÊN B (CREATOR):</h3>
${personBlock(cr)}
<p>TikTok ID: ${blank(
    cr.tiktokId ? `@${cr.tiktokId.replace(/^@/, '')}` : '',
    180
  )} &nbsp; UID TikTok: ${blank(cr.tiktokUid, 180)}</p>
${
  data.guardian
    ? `<h3>NGƯỜI ĐẠI DIỆN THEO PHÁP LUẬT CỦA BÊN B (Bên B chưa đủ 18 tuổi):</h3>${personBlock(
        data.guardian
      )}
<p>Quan hệ với Bên B: ${blank(
        data.guardian.relation,
        160
      )}. Người đại diện đồng ý cho Bên B ký kết và cùng chịu trách nhiệm thực hiện hợp đồng này.</p>`
    : ''
}

<p>Hai bên thoả thuận ký hợp đồng với các điều khoản sau:</p>

${h('Đối tượng hợp đồng')}
<p>1. Bên A hỗ trợ Bên B bộ thiết bị livestream liệt kê tại <b>Phụ lục 01</b> để Bên B hoạt động LIVE trên nền tảng TikTok trong hệ thống quản lý của Bên A.</p>
<p>2. Tổng giá trị thiết bị và dịch vụ: <b>${formatVnd(s.total)}</b> (${vndInWords(
    s.total
  )}), trong đó:</p>
<p>&nbsp;&nbsp;a) Bên A hỗ trợ: <b>${formatVnd(s.company)}</b> (${vndInWords(
    s.company
  )}), tương đương ${s.companyRate}% tổng giá trị;</p>
<p>&nbsp;&nbsp;b) Bên B thanh toán: <b>${formatVnd(s.creator)}</b> (${vndInWords(s.creator)}).</p>
${
  s.travelSupport
    ? `<p>3. Ngoài ra Bên A chi trả chi phí đi lại, vận chuyển, lắp đặt cho Bên B: <b>${formatVnd(
        s.travelSupport
      )}</b> (${vndInWords(s.travelSupport)}).</p>`
    : ''
}
${
  extraArt1
    ? `<p>${s.travelSupport ? 4 : 3}. ${
        includedLines.length
          ? `Các hạng mục <b>tặng kèm</b> (thiết lập, quà tặng, game tương tác — ghi "Tặng kèm" tại Phụ lục 01): ${includedLines
              .map((l) => esc(l.name))
              .join('; ')}. Bên A hỗ trợ <b>miễn phí</b> vì Bên B là Creator của ${esc(
              company.shortName || 'Bên A'
            )}; game / dịch vụ tính theo tháng, năm được miễn phí trong thời gian Bên B còn là Creator của Bên A. `
          : ''
      }${
        advanceLines.length
          ? `Riêng ${advanceLines
              .map((l) => `<b>${esc(l.name)}</b> (${formatVnd(l.price * l.qty)})`)
              .join(
                '; '
              )}: Bên A <b>ứng trước</b> tiền mua, Bên B <b>trả lại toàn bộ</b> theo Điều 3 (đã tính trong phần Bên B thanh toán tại khoản 2 Điều này).`
          : ''
      }</p>`
    : ''
}
<p>${
    (s.travelSupport ? 4 : 3) + extraArt1
  }. <b>Quan hệ giữa hai bên là quan hệ dân sự</b>, Bên A hỗ trợ trên tinh thần tự nguyện; <b>không phải quan hệ lao động</b>. Bên B tự chủ thời gian và nội dung LIVE trong khuôn khổ cam kết tại Điều 5, không hưởng lương từ Bên A và không thuộc diện tham gia bảo hiểm xã hội theo hợp đồng này.</p>
<p>${
    (s.travelSupport ? 5 : 4) + extraArt1
  }. Tài khoản TikTok là <b>tài khoản cá nhân</b> của Bên B. Mọi vấn đề về tài khoản xử lý theo quy định của TikTok${
    c.agencyTermYears
      ? ` và thoả thuận gia nhập Đại lý (Agency) mà Bên B đã chấp nhận trên TikTok, thời hạn <b>${c.agencyTermYears} năm</b> theo tài khoản cá nhân`
      : ''
  }. Thiết bị và các khoản tiền hỗ trợ quy đổi ra tiền xử lý theo hợp đồng này và pháp luật Việt Nam.</p>

${h('Quyền sở hữu thiết bị')}
<p>1. ${
    supportedLines.length ? 'Phần thiết bị do Bên A hỗ trợ' : 'Thiết bị'
  } thuộc quyền sở hữu của Bên A. Bên A giao cho Bên B <b>mượn để sử dụng</b> theo Điều 494 Bộ luật Dân sự, <b>tối đa 12 tháng</b>; thời hạn cam kết ${
    c.commitMonths
  } tháng kể từ ngày bàn giao.</p>
${
  c.ownershipTransfer
    ? `<p>2. Hết thời hạn cam kết, nếu Bên B đã thực hiện đầy đủ Điều 5 và không vi phạm Điều 8, Bên A <b>tặng cho</b> Bên B phần thiết bị đã hỗ trợ (tặng cho có điều kiện theo Điều 462 Bộ luật Dân sự). Hai bên lập biên bản xác nhận chuyển quyền sở hữu.</p>`
    : `<p>2. Hết thời hạn cam kết, phần thiết bị do Bên A hỗ trợ vẫn thuộc quyền sở hữu của Bên A; hai bên thoả thuận gia hạn hoặc Bên B trả lại thiết bị.</p>`
}
${
  creatorLines.length && s.creator > 0
    ? `<p>3. Phần giá trị Bên B thanh toán là <b>mua trả dần</b> (Điều 453 Bộ luật Dân sự). Bên A <b>bảo lưu quyền sở hữu</b> (Điều 331 Bộ luật Dân sự) đối với toàn bộ thiết bị cho đến khi Bên B thanh toán đủ.</p>`
    : ''
}
<p>${
    creatorLines.length && s.creator > 0 ? 4 : 3
  }. Khi chưa có quyền sở hữu, Bên B <b>không được</b> bán, cho thuê, cho mượn, cầm cố, thế chấp, đổi linh kiện hoặc gỡ tem mã tài sản của thiết bị; không mang thiết bị khỏi địa chỉ lắp đặt quá 07 ngày nếu chưa báo Bên A bằng văn bản hoặc tin nhắn. Bên B <b>không được cho mượn, hoặc để Creator / người khác không thuộc Đại lý của Bên A dùng thiết bị để phát sóng hay làm việc</b>, dù một lần hay thường xuyên.</p>

${h('Thanh toán')}
<p>${++k}. Bên B trả trước khi nhận thiết bị: <b>${formatVnd(
    s.upfront
  )}</b>. Đây là khoản <b>thanh toán trước</b> một phần giá trị Bên B phải trả, không phải tiền đặt cọc.</p>
<p>${++k}. Số còn lại: <b>${formatVnd(s.remaining)}</b>${
    p.installmentMonths > 0 && s.remaining > 0
      ? `, trả dần trong ${p.installmentMonths} tháng, mỗi tháng <b>${formatVnd(
          s.monthly
        )}</b> (tháng cuối trả phần còn lại), hạn chót ngày 10 hằng tháng`
      : s.remaining > 0
      ? ', trả một lần trong 30 ngày kể từ ngày bàn giao'
      : ''
  }.</p>
<p>${++k}. Cách thanh toán: ${methodText}.</p>
<p>${++k}. Trong thời gian sử dụng thiết bị, Bên B đồng ý trích <b>${
    c.incomeSharePercent ? `${c.incomeSharePercent}%` : `${blank('', 50)}%`
  }</b> thu nhập LIVE của tài khoản TikTok cá nhân (theo số liệu Đại lý / Bên A quản lý) để <b>trả dần</b> phần giá trị Bên B phải thanh toán và các khoản phải hoàn theo hợp đồng này. Tổng số trích không vượt quá số Bên B còn phải trả; trả đủ thì dừng trích. Khoản trích là tiền trả cho thiết bị, không phải phí dịch vụ Đại lý.</p>
${
  c.allowDeductIncome
    ? `<p>${++k}. Bên B đồng ý để Bên A <b>bù trừ</b> mọi khoản Bên B phải trả hoặc phải hoàn theo hợp đồng này vào thưởng, thu nhập mà Bên A chi trả cho Bên B (Điều 378 Bộ luật Dân sự). Mỗi lần bù trừ Bên A gửi Bên B bảng kê chi tiết.</p>`
    : ''
}
${
  c.allowBuyout
    ? `<p>${++k}. Trong thời gian sử dụng, Bên B có quyền đề nghị <b>mua đứt thiết bị theo 100% giá hoá đơn</b> mua thiết bị (Bên A cung cấp bản sao hoá đơn), trừ số tiền Bên B đã trả cho thiết bị đó. Bên B được trả một lần hoặc <b>chia nhiều đợt theo nhu cầu</b>, lịch trả ghi bằng văn bản. Trả đủ thì thiết bị thuộc sở hữu của Bên B và Bên B hết nghĩa vụ hoàn trả thiết bị đó.</p>`
    : ''
}
<p>${++k}. Chậm thanh toán thì Bên B trả thêm lãi chậm trả ${
    c.lateInterestPercentYear
  }%/năm trên số tiền chậm trả, tính theo số ngày chậm (Điều 357 và Điều 468 Bộ luật Dân sự).</p>

${h('Bàn giao, lắp đặt')}
<p>1. Bên A (thông qua kỹ thuật viên) giao, lắp đặt và hướng dẫn sử dụng thiết bị tại: ${blank(
    data.deliveryAddress,
    380
  )}.</p>
<p>2. Khi bàn giao, hai bên ký <b>Biên bản bàn giao (Phụ lục 02)</b> ghi tên, số serial, tình trạng từng thiết bị (<b>mới 100%</b> hay <b>đã qua sử dụng</b>), kèm ảnh / video. Biên bản là căn cứ khi hoàn trả.</p>
<p>3. Thiết bị được bảo hành theo chính sách của hãng / nơi bán. Bên A hỗ trợ Bên B làm thủ tục bảo hành.</p>

${h('Cam kết hoạt động')}
<p>1. Trong thời hạn cam kết ${c.commitMonths} tháng, mỗi tháng Bên B phát sóng LIVE tối thiểu <b>${
    c.minLiveDaysPerMonth ? `${c.minLiveDaysPerMonth}` : blank('', 60)
  } ngày</b> và <b>${c.minLiveHoursPerMonth ? `${c.minLiveHoursPerMonth}` : blank('', 60)} giờ</b>${
    c.minLiveDaysPerMonth || c.minLiveHoursPerMonth
      ? ''
      : ' (hai bên tự thoả thuận và ghi vào chỗ trống)'
  }. Số liệu tính theo dữ liệu TikTok LIVE mà Bên A quản lý. Tháng không đạt gọi là "tháng không hoạt động".</p>
<p>2. Khi phát sóng, Bên B tuân thủ <b>Nguyên tắc cộng đồng và quy định LIVE của TikTok</b>, pháp luật Việt Nam về thông tin trên mạng (Nghị định 147/2024/NĐ-CP) và quảng cáo; không phát nội dung phản cảm, sai sự thật, vi phạm thuần phong mỹ tục, bản quyền hoặc quyền riêng tư của người khác, để không ảnh hưởng đến hình ảnh của Bên B và của Bên A.</p>
<p>3. Bên B bị TikTok hạn chế hoặc khoá LIVE do vi phạm quy định nền tảng thì thời gian bị hạn chế vẫn tính là "tháng không hoạt động". Vi phạm gây thiệt hại thực tế cho uy tín, hình ảnh của Bên A thì Bên B bồi thường theo khoản 4 Điều 8.</p>
<p>4. Bên B giữ liên lạc với Bên A qua số điện thoại, Zalo và ứng dụng HP Media đã đăng ký. Đổi số điện thoại hoặc địa chỉ thì báo Bên A trong 03 ngày.</p>
<p>5. Bên B được tạm nghỉ vì lý do chính đáng (ốm đau, thai sản, việc gia đình đột xuất, sự kiện bất khả kháng) nếu báo trước hoặc có giấy tờ chứng minh. Thời gian tạm nghỉ được cộng thêm vào thời hạn cam kết.</p>

${h('Quyền và nghĩa vụ của Bên A')}
<p>1. Giao đúng, đủ thiết bị; lắp đặt, đào tạo; hỗ trợ kỹ thuật trong thời hạn cam kết.</p>
<p>2. Được kiểm tra tình trạng thiết bị định kỳ, có báo trước và được Bên B đồng ý về thời gian. Được yêu cầu Bên B gửi ảnh / video thiết bị.</p>
<p>3. Được đòi lại thiết bị khi Bên B dùng sai mục đích, không hoạt động, hoặc vi phạm hợp đồng (Điều 499 Bộ luật Dân sự); được yêu cầu trả tiền, phạt vi phạm và bồi thường theo Điều 8.</p>
<p>4. Bảo mật thông tin cá nhân của Bên B, chỉ dùng cho hợp đồng này. Bên A <b>không giữ</b> giấy tờ tuỳ thân bản gốc của Bên B.</p>

${h('Quyền và nghĩa vụ của Bên B')}
<p>1. Được sử dụng thiết bị để LIVE; được hỗ trợ kỹ thuật, bảo hành.</p>
<p>2. Giữ gìn, bảo quản thiết bị như tài sản của mình (Điều 496 Bộ luật Dân sự). Báo Bên A trong 24 giờ khi thiết bị mất, hỏng.</p>
<p>3. <b>Hoàn trả không tính khấu hao mới / cũ:</b> khi Bên B trả lại thiết bị cho Bên A (kể cả thiết bị nhận lúc còn mới 100%), Bên A nhận lại theo hiện trạng, <b>không trừ tiền, không tính khấu hao</b> vì thiết bị từ mới thành cũ hay hao mòn tự nhiên khi dùng đúng cách.</p>
<p>4. Bên B <b>chỉ phải bồi thường</b> khi thiết bị hư hỏng do <b>rơi rớt, va đập, vào nước, cháy nổ do dùng sai cách</b> hoặc lỗi khác của Bên B mà nhà sản xuất / nơi bán <b>từ chối bảo hành</b>. Mức bồi thường là chi phí sửa chữa thực tế có hoá đơn; không sửa được, hoặc thiết bị bị mất, thì bồi thường theo <b>Bảng mức bồi thường tại Phụ lục 01</b>: thiết bị nhận <b>mới 100%</b> bồi thường <b>${
    c.compensateNewPercent
  }%</b> giá hoá đơn; thiết bị nhận lúc <b>đã qua sử dụng</b> bồi thường <b>${
    c.compensateUsedPercent
  }%</b> giá hoá đơn; trừ số tiền Bên B đã trả cho thiết bị đó. Hư hỏng còn được bảo hành thì Bên A làm bảo hành, Bên B không phải trả tiền.</p>
<p>5. Thanh toán đúng, đủ, đúng hạn theo Điều 3; trả lại thiết bị đúng hạn và đúng tình trạng khi phải hoàn trả theo hợp đồng.</p>

${h('Hoàn trả thiết bị và chi phí; vi phạm và phạt vi phạm')}
<p>1. <b>Không sử dụng nữa:</b> Bên B không muốn tiếp tục thì báo Bên A và trả lại phần thiết bị Bên A hỗ trợ trong ${
    c.returnDays
  } ngày. Muốn giữ thiết bị thì Bên B mua đứt theo khoản mua thiết bị tại Điều 3 (100% giá hoá đơn, trừ số đã trả).</p>
<p>2. <b>Không hoạt động ${
    c.inactivityMonths
  } tháng liên tục</b> (theo khoản 1 Điều 5): Bên A có quyền chấm dứt hợp đồng. Bên B phải <b>hoàn trả toàn bộ thiết bị</b>${
    s.travelSupport
      ? ` và <b>hoàn lại chi phí đi lại / lắp đặt ${formatVnd(s.travelSupport)}</b>`
      : ' và hoàn lại chi phí đi lại / lắp đặt Bên A đã chi (nếu có)'
  } trong ${c.returnDays} ngày kể từ ngày nhận thông báo.</p>
<p>3. Các trường hợp sau là <b>vi phạm hợp đồng</b>:</p>
<p>&nbsp;&nbsp;a) Quá hạn mà không trả lại thiết bị hoặc không trả tiền theo khoản 1, khoản 2 Điều này hoặc theo Điều 3;</p>
<p>&nbsp;&nbsp;b) Không liên lạc được quá <b>${
    c.unreachableDays
  } ngày liên tục</b> qua tất cả các kênh đã đăng ký mà không có lý do chính đáng;</p>
<p>&nbsp;&nbsp;c) Bán, cho thuê, cho mượn, cầm cố, thế chấp thiết bị, hoặc làm việc khác trái khoản ${
    creatorLines.length && s.creator > 0 ? 4 : 3
  } Điều 2;</p>
<p>&nbsp;&nbsp;d) Cung cấp thông tin cá nhân sai sự thật khi ký hợp đồng${
    c.leaveAgencyIsBreach ? ';' : '.'
  }</p>${
    c.leaveAgencyIsBreach
      ? `
<p>&nbsp;&nbsp;đ) Rời Đại lý của Bên A trên TikTok, hoặc chuyển LIVE sang tài khoản khác ngoài Đại lý, khi chưa trả đủ hoặc chưa hoàn trả thiết bị.</p>`
      : ''
  }
<p>4. Khi Bên B vi phạm, Bên A có quyền đơn phương chấm dứt hợp đồng (Điều 428 Bộ luật Dân sự). Bên B phải:</p>
<p>&nbsp;&nbsp;a) Trả lại ngay toàn bộ thiết bị Bên B chưa có quyền sở hữu. Thiết bị bị mất, hoặc hư hỏng thuộc trường hợp phải bồi thường tại khoản 4 Điều 7, thì Bên B trả theo khoản 4 Điều 7; nếu Bên B cố ý làm mất hoặc hư hỏng thì trả theo giá mua mới tại thời điểm bồi thường;</p>
<p>&nbsp;&nbsp;b) Hoàn lại chi phí đi lại, lắp đặt Bên A đã chi và trả nốt số tiền Bên B còn nợ;</p>
<p>&nbsp;&nbsp;c) Chịu <b>phạt vi phạm ${
    c.penaltyPercent
  }%</b> giá trị phần nghĩa vụ bị vi phạm. Ngoài tiền phạt, Bên B <b>bồi thường toàn bộ thiệt hại thực tế</b> cho Bên A (khoản 3 Điều 418 Bộ luật Dân sự);</p>
<p>&nbsp;&nbsp;d) Chịu các chi phí hợp lý để thu hồi: vận chuyển, lập vi bằng, chi phí tố tụng theo quy định.</p>
<p>5. Bên B tạm thời không có khả năng chi trả <b>không được miễn</b> nghĩa vụ. Hai bên có thể lập kế hoạch trả dần bằng văn bản. Bên B không thực hiện kế hoạch trả dần thì Bên A có quyền khởi kiện.</p>

${h('Giải quyết tranh chấp')}
<p>1. Tranh chấp trước hết được giải quyết bằng thương lượng, hoà giải trong 15 ngày kể từ ngày một bên gửi yêu cầu.</p>
<p>2. Thương lượng, hoà giải không thành thì một bên có quyền khởi kiện tại Toà án có thẩm quyền.${
    c.disputeAtCompanyCourt
      ? ' Theo điểm b khoản 1 Điều 39 Bộ luật Tố tụng dân sự 2015, hai bên <b>thoả thuận chọn Toà án nhân dân nơi Bên A có trụ sở</b> giải quyết.'
      : ''
  }</p>
<p>3. Bên B có dấu hiệu gian dối, bỏ trốn, bán hoặc cầm cố thiết bị, hoặc đến hạn có điều kiện mà cố tình không trả nhằm chiếm đoạt, thì Bên A có quyền trình báo cơ quan công an có thẩm quyền theo quy định của pháp luật hình sự. Việc trình báo không làm mất quyền yêu cầu bồi thường dân sự của Bên A.</p>

${h('Thông tin cá nhân')}
<p>Bên B đồng ý để Bên A thu thập và xử lý họ tên, số CCCD, ảnh chụp CCCD, địa chỉ, số điện thoại, tài khoản TikTok và dữ liệu LIVE của Bên B. Thông tin chỉ dùng để ký, thực hiện và giải quyết tranh chấp hợp đồng này. Bên A lưu thông tin trong thời hạn hợp đồng và thời hiệu khởi kiện theo Luật Bảo vệ dữ liệu cá nhân.</p>

${
  extra.length
    ? `${h('Thoả thuận khác')}${extra.map((t, i) => `<p>${i + 1}. ${esc(t)}</p>`).join('')}`
    : ''
}

${h('Hiệu lực')}
<p>1. Hợp đồng có hiệu lực từ ngày ký đến khi các bên hoàn thành mọi nghĩa vụ. Phụ lục 01 và Phụ lục 02 là một phần không tách rời của hợp đồng.</p>
<p>2. Bên B xác nhận đã đọc kỹ, được giải thích đầy đủ và đồng ý toàn bộ nội dung hợp đồng trước khi ký.</p>
<p>3. Hồ sơ kèm hợp đồng: <b>bản chụp CCCD hai mặt</b> của Bên B (và người đại diện, nếu có), có chữ ký xác nhận của Bên B; ảnh / video lúc bàn giao. Bên A chỉ xem bản gốc để đối chiếu, không giữ bản gốc.</p>
<p>4. Hợp đồng lập thành 02 bản có giá trị như nhau, mỗi bên giữ 01 bản. Nếu hai bên ký điện tử thì bản điện tử có giá trị như bản giấy (Luật Giao dịch điện tử 2023).</p>

<table class="sign"><tr>
  <td><b>ĐẠI DIỆN BÊN A</b><br><i>(Ký, ghi rõ họ tên, đóng dấu)</i><br><br><br><br><br>${esc(
    company.representative
  )}</td>
  <td><b>BÊN B</b><br><i>(Ký, ghi rõ họ tên)</i><br><br><br><br><br>${esc(cr.fullName)}</td>
</tr>${
    data.guardian
      ? `<tr><td></td><td><b>NGƯỜI ĐẠI DIỆN CỦA BÊN B</b><br><i>(Ký, ghi rõ họ tên)</i><br><br><br><br><br>${esc(
          data.guardian.fullName
        )}</td></tr>`
      : ''
  }</table>

<div class="pb"></div>
<h2>PHỤ LỤC 01 — BẢNG THIẾT BỊ VÀ CHIA CHI PHÍ</h2>
<div class="center">(Kèm hợp đồng số ${esc(data.contractNo || '..........')}, báo giá ${esc(
    code
  )})</div>
<table class="grid">
  <tr><th>STT</th><th>Tên thiết bị / dịch vụ</th><th>Tình trạng</th><th>SL</th><th>Thành tiền</th><th>Bên A hỗ trợ</th><th>Bên A trả</th><th>Bên B trả</th></tr>
  ${lineRows}
  <tr><th colspan="4">TỔNG</th><th class="r">${formatVnd(s.total)}</th><th>${
    s.companyRate
  }%</th><th class="r">${formatVnd(s.company)}</th><th class="r">${formatVnd(s.creator)}</th></tr>
</table>
<p><b>Bảng mức bồi thường</b> — chỉ áp dụng khi thiết bị <b>bị mất</b>, hoặc <b>hư hỏng không sửa được mà không được bảo hành</b> do lỗi của Bên B (khoản 4 Điều 7). <b>Trả lại thiết bị bình thường thì không tính khấu hao, không trừ tiền.</b></p>
<table class="grid">
  <tr><th>STT</th><th>Thiết bị</th><th>Tình trạng khi nhận</th><th>Giá hoá đơn × SL</th><th>Mức bồi thường</th><th>Số tiền bồi thường</th></tr>
  ${physicalLines
    .map(
      (l, i) =>
        `<tr><td class="c">${i + 1}</td><td>${esc(l.name)}</td><td class="c">${
          CONDITION_LABEL[l.condition ?? 'NEW']
        }</td><td class="r">${formatVnd(l.price * l.qty)}</td><td class="c">${
          l.condition === 'USED' ? c.compensateUsedPercent : c.compensateNewPercent
        }%</td><td class="r">${formatVnd(compensationValue(l, c))}</td></tr>`
    )
    .join('')}
  <tr><th colspan="5">TỔNG (nếu mất toàn bộ)</th><th class="r">${formatVnd(
    compensationTotal
  )}</th></tr>
</table>
<p class="note">Lý do: thiết bị nhận mới thì Bên A phải mua mới để thay thế nên bồi thường đủ giá mua; thiết bị nhận lúc đã cũ được tính giảm ${
    100 - c.compensateUsedPercent
  }%. Số tiền Bên B đã trả cho thiết bị nào thì được trừ vào mức bồi thường của thiết bị đó.</p>

<div class="pb"></div>
${handoverBody({
  code,
  data,
  company,
  title: 'PHỤ LỤC 02 — BIÊN BẢN BÀN GIAO VÀ NGHIỆM THU THIẾT BỊ',
})}
</body></html>`;
}

/** Tải bản Word (.doc) — Word mở trực tiếp HTML; người dùng "Lưu thành" .docx nếu cần. */
export function contractWordBlob(html: string): Blob {
  const wordHtml = html.replace(
    '<html lang="vi">',
    '<html lang="vi" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">'
  );
  return new Blob(['\ufeff', wordHtml], { type: 'application/msword' });
}
